/**
 * Deep-link allow-list helper.
 *
 * Defense in depth: before any {@code window.location} redirect the SPA re-validates the
 * redirect URL against {@code VITE_ALLOWED_DEEPLINK_PREFIXES} — so a tampered or stale deep
 * link dead-ends benignly instead of shipping the member to a hostile scheme.
 *
 * Match semantics: case-sensitive {@link String#startsWith} plus protocol/origin/path checks.
 */

import { appConfig } from '../../../../shared/config/app-config';

function loadAllowedPrefixes(): string[] {
  return appConfig.allowedDeeplinkPrefixes
    .split(',')
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
}

const DEFAULT_PREFIXES: readonly string[] = Object.freeze(loadAllowedPrefixes());

export interface DeeplinkValidator {
  isAllowed(url: string | null | undefined): boolean;
  prefixes(): readonly string[];
}

/** Default validator using the prefixes from VITE_ALLOWED_DEEPLINK_PREFIXES. */
export const deeplinkValidator: DeeplinkValidator = createValidator(DEFAULT_PREFIXES);

/**
 * Construct an explicit validator with a fixed prefix list. Useful in tests so we don't
 * depend on the build-time env var.
 */
export function createValidator(allowedPrefixes: readonly string[]): DeeplinkValidator {
  const prefixes = allowedPrefixes.filter((p) => p.length > 0);

  const allowedEntries = prefixes
    .map((prefix) => {
      try {
        return { raw: prefix, url: new URL(prefix) };
      } catch {
        return null;
      }
    })
    .filter((entry): entry is { raw: string; url: URL } => entry !== null);

  return {
    isAllowed(url: string | null | undefined): boolean {
      if (!url || url.trim().length === 0) return false;

      let candidate: URL;
      try {
        candidate = new URL(url);
      } catch {
        return false;
      }

      for (const { raw, url: allowed } of allowedEntries) {
        if (!url.startsWith(raw)) continue;

        if (candidate.protocol !== allowed.protocol) continue;

        if (allowed.origin !== 'null') {
          if (candidate.origin !== allowed.origin) continue;

          const allowedPath = allowed.pathname.endsWith('/')
            ? allowed.pathname
            : `${allowed.pathname}/`;
          const candidatePath = candidate.pathname;

          if (candidatePath === allowed.pathname || candidatePath.startsWith(allowedPath)) {
            return true;
          }
          continue;
        }

        const allowedHrefPrefix = allowed.href.endsWith('/') ? allowed.href : `${allowed.href}/`;
        if (candidate.href === allowed.href || candidate.href.startsWith(allowedHrefPrefix)) {
          return true;
        }
      }

      return false;
    },
    prefixes(): readonly string[] {
      return prefixes;
    },
  };
}
